import { useState } from "react";
import axios from "axios";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { formatDateTime } from "@/components/admin/ui";
import { LEAD_STATUS_META, LOST_REASON_LABELS, channelForContactMethod } from "./salesMeta";

export const ActivityPanel = ({ lead, channels, onRefresh, onError, API }) => {
  const [busy, setBusy] = useState(false);
  const [showLostReasons, setShowLostReasons] = useState(false);

  const channelKey = channelForContactMethod(lead.preferred_contact_method);
  const channelConnected = channelKey ? channels?.[channelKey] : null;
  const isClosed = lead.status === "won" || lead.status === "lost";

  const setStatus = async (status, lost_reason) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/org/sales/leads/${lead.lead_id}/status`, { status, lost_reason }, { withCredentials: true });
      setShowLostReasons(false);
      await onRefresh();
    } catch (err) { onError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const activity = [...(lead.activity || [])].reverse();

  return (
    <div className="flex h-full flex-col" data-testid="lead-activity-panel">
      <div className="space-y-2 border-b border-slate-200 pb-4 dark:border-white/10">
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Quick Actions</p>
        <button
          type="button" disabled={!channelKey || !channelConnected} data-testid="lead-action-contact-customer"
          title={!channelKey ? "No preferred contact method on file" : !channelConnected ? `${channelKey === "sms" ? "SMS" : "Email"} not connected` : undefined}
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-left text-xs font-bold text-slate-700 transition-colors duration-150 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
        >
          Contact Customer
        </button>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" disabled={busy || isClosed} onClick={() => setStatus("active")} data-testid="lead-action-mark-active" className="rounded-xl border border-slate-200 px-2 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">Mark Active</button>
          <button type="button" disabled={busy || isClosed} onClick={() => setStatus("waiting_on_customer")} data-testid="lead-action-mark-waiting" className="rounded-xl border border-slate-200 px-2 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">Waiting on Customer</button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" disabled={busy || isClosed} onClick={() => setStatus("won")} data-testid="lead-action-mark-won" className="rounded-xl border border-emerald-200 bg-emerald-50 px-2 py-2 text-xs font-bold text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400">Mark Won</button>
          <button type="button" disabled={busy || isClosed} onClick={() => setShowLostReasons((v) => !v)} data-testid="lead-action-mark-lost" className="rounded-xl border border-red-200 bg-red-50 px-2 py-2 text-xs font-bold text-red-600 hover:bg-red-100 disabled:opacity-40 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400">Mark Lost</button>
        </div>
        {showLostReasons && (
          <div className="rounded-xl border border-slate-200 p-2 dark:border-white/10" data-testid="lead-lost-reason-picker">
            {Object.entries(LOST_REASON_LABELS).map(([k, label]) => (
              <button key={k} type="button" onClick={() => setStatus("lost", k)} data-testid={`lead-lost-reason-${k}`} className="block w-full rounded-lg px-2 py-1.5 text-left text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-white/10">
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 flex-1 overflow-y-auto">
        <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Activity</p>
        <div className="space-y-3" data-testid="lead-activity-feed">
          {activity.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">No activity yet.</p>
          ) : activity.map((a, i) => (
            <div key={i} className="flex items-start gap-2.5 text-sm" data-testid={`lead-activity-item-${i}`}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#0052FF]" />
              <div>
                <p className="font-semibold text-slate-900 dark:text-white">{a.label}</p>
                <p className="text-xs text-slate-400">{a.actor_name} · {formatDateTime(a.ts)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
